import { Injectable, TemplateRef, Type } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { NzDrawerOptions, NzDrawerService } from 'ng-zorro-antd/drawer';
import { Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { WindowRef } from '../../../core/window';
import { DrawerOptions, DrawerPlacement, DrawerWidth } from './drawer-options';
import { DrawerRef } from './drawer-ref';

const defaultDrawerOptions: Partial<NzDrawerOptions> = {
  nzClosable: false,
  nzWidth: DrawerWidth.Default,
  nzZIndex: 1052,
  nzCloseOnNavigation: true, // nzCloseOnNavigation doesn't seem to work
  nzPlacement: DrawerPlacement.Default,
  nzOffsetY: 0,
  nzHeight: 'auto',
};

/**
 * A service to handle drawers
 */
@Injectable({
  providedIn: 'root',
})
export class DrawerService {
  private openDrawer: DrawerRef | null;

  constructor(
    private nzDrawerService: NzDrawerService,
    router: Router,
    private windowRef: WindowRef
  ) {
    router.onSameUrlNavigation = 'reload';
    router.events.subscribe({
      next: (event) => {
        if (event instanceof NavigationStart) {
          if (this.openDrawer?.nzCloseOnNavigation) {
            this.closeOpenDrawer();
          }
        }
      },
    });
  }

  getDrawerTopOffsetY(): Observable<number | null> {
    if (!this.windowRef.isBrowser()) {
      return of(null);
    }

    const headerElement = this.windowRef.document?.getElementById('header');
    const headerElementBottom = headerElement?.getBoundingClientRect()?.bottom;

    return of(headerElementBottom ?? 0);
  }

  isDrawerOpen(template: TemplateRef<any> | Type<any>) {
    return template === this.openDrawer?.nzContent;
  }

  /**
   * Opens a drawer if there is no drawer currently opening
   */
  open(options: DrawerOptions): Observable<DrawerRef | null> {
    if (this.isDrawerOpen(options?.template)) {
      this.closeOpenDrawer();
      return of(null);
    }

    this.closeOpenDrawer();
    return this.getDrawerTopOffsetY().pipe(
      map((offsetY) => {
        if (options.placement === DrawerPlacement.Top) {
          options.offsetY = offsetY;
          options.zIndex = 9; //The header is 10, and we need it to be underneath the header
        }

        const newDrawer = this.nzDrawerService.create({
          ...defaultDrawerOptions,
          nzContent: options.template,
          nzContentParams: options.params,
          nzWidth: options.width ?? defaultDrawerOptions.nzWidth,
          nzCloseOnNavigation:
            options.closeOnNavigation !== undefined ? options.closeOnNavigation : defaultDrawerOptions.nzCloseOnNavigation,
          nzPlacement: options.placement ?? defaultDrawerOptions.nzPlacement,
          nzWrapClassName: options.wrapClassName,
          nzHeight: options.height ?? defaultDrawerOptions.nzHeight,
          nzOffsetY: options.offsetY ?? defaultDrawerOptions.nzOffsetY,
          nzZIndex: options.zIndex ?? defaultDrawerOptions.nzZIndex,
          nzBodyStyle: {
            '--py-header-offset': `${offsetY}px`,
            ...(options?.placement === DrawerPlacement.Top ? { 'max-height': 'calc(100vh - var(--py-header-offset)' } : {}),
          },
        });
        this.openDrawer = newDrawer;

        this.handleDrawerEvents(newDrawer);
        return newDrawer;
      })
    );
  }

  private handleDrawerEvents(drawer: DrawerRef): void {
    drawer.afterClose
      .pipe(
        filter(() => this.isDrawerOpen(drawer?.nzContent)),
        take(1)
      )
      .subscribe(() => {
        this.openDrawer = null;
      });

    drawer.afterOpen
      .pipe(
        filter(() => !this.openDrawer),
        take(1)
      )
      .subscribe(() => (this.openDrawer = drawer));
  }

  closeOpenDrawer(): void {
    this.openDrawer?.close();
  }

  getOpenDrawer(): DrawerRef {
    return this.openDrawer;
  }
}
